import { Delete as DeleteIcon } from '@mui/icons-material'
import { Button } from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { Confirm, useNotify, useRecordContext, useRedirect, useRefresh, useTranslate, useUpdate } from 'react-admin'

import type { HistoricalInspection } from '../../models/inspection'

export interface SoftDeleteButtonProps {
  /** Explicit record; falls back to the React Admin record context (e.g. list rows). */
  record?: HistoricalInspection
  /**
   * Marks the detail-page usage. When true, redirect to the list after a successful
   * delete and label the button "Eliminar inspección"; otherwise refresh the current
   * view in place and label it "Eliminar" (used in the list).
   */
  redirectOnSuccess?: boolean
}

/**
 * Soft-deletes a historical inspection by setting `is_deleted = true` through the
 * dataProvider update (a PostgREST PATCH), guarded by a confirmation dialog.
 * Used from both the list rows (refresh in place) and the detail page header
 * (redirect to the list). The record itself is preserved in the database; only
 * the `is_deleted` flag flips, so it disappears from every filtered view.
 */
export const SoftDeleteButton: FC<SoftDeleteButtonProps> = ({ record: recordProp, redirectOnSuccess = false }) => {
  const contextRecord = useRecordContext<HistoricalInspection>()
  const record = recordProp ?? contextRecord
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const [open, setOpen] = useState(false)
  const [update, { isPending }] = useUpdate()

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // Prevent the surrounding datagrid row from navigating to the show view.
    event.stopPropagation()
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleConfirm = useCallback(() => {
    if (!record) return
    void update(
      'historical_inspections',
      { id: record.id, data: { is_deleted: true }, previousData: record },
      {
        onSuccess: () => {
          notify('pages.historical_inspections.delete.success', { type: 'info' })
          setOpen(false)
          if (redirectOnSuccess) {
            redirect('list', 'historical_inspections')
          } else {
            refresh()
          }
        },
        onError: () => {
          notify('pages.historical_inspections.delete.error', { type: 'error' })
          setOpen(false)
        },
      },
    )
  }, [record, update, notify, refresh, redirect, redirectOnSuccess])

  if (!record) return null

  // The detail page shows the button in a header action stack, away from the row
  // that identifies the inspection, so it spells the subject out ("Eliminar
  // inspección"); list rows keep the terser "Eliminar" since the row supplies it.
  const label = translate(
    redirectOnSuccess ? 'pages.historical_inspections.delete.detail_label' : 'pages.historical_inspections.delete.label',
  )

  return (
    <>
      <Button color="error" variant="text" size="small" onClick={handleOpen} startIcon={<DeleteIcon />} aria-label={label}>
        {label}
      </Button>
      <Confirm
        isOpen={open}
        loading={isPending}
        title="pages.historical_inspections.delete.confirm_title"
        content="pages.historical_inspections.delete.confirm_content"
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </>
  )
}
